import { ArrowLeft, Calendar, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getEnrichmentStatusStats,
    EnrichmentStatusStats,
} from "../../../interfaces/events";
import {
    getEstablishmentStatusStats,
    EstablishmentStatusStats,
    checkSirenStatus,
    SirenCheckResult,
    getClosingCheckerPending,
    ClosingCheckerPendingItem,
} from "../../../interfaces/establishments";
import {
    getBodaccDetailStats,
    BodaccDetailStats,
} from "../../../interfaces/enrichmentRules";
import {
    getServicesOverview,
    ServiceOverviewItem,
} from "../../../interfaces/servicesAdmin";

// ── Shared helpers ─────────────────────────────────────────────────────────────

function DFSKpiCard({
    label,
    value,
    total,
    valueColor,
}: {
    label: string;
    value: number;
    total: number;
    valueColor: string;
}) {
    const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${valueColor}`}>
                {value.toLocaleString("fr-FR")}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{pct}% du total</p>
        </div>
    );
}

function StatCard({
    label,
    value,
    sub,
    valueColor,
}: {
    label: string;
    value: number | string;
    sub?: string;
    valueColor?: string;
}) {
    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${valueColor ?? "text-gray-900"}`}>
                {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
            </p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function stackedBarOptions(labels: string[], colors: string[]): ApexCharts.ApexOptions {
    return {
        chart: {
            type: "bar",
            stacked: true,
            toolbar: { show: false },
            fontFamily: "inherit",
            background: "transparent",
        },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
        colors,
        dataLabels: { enabled: false },
        xaxis: {
            categories: labels,
            labels: { style: { colors: "#6b7280", fontSize: "11px" } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#6b7280", fontSize: "11px" } } },
        grid: { borderColor: "#f3f4f6", strokeDashArray: 3, xaxis: { lines: { show: false } } },
        tooltip: { theme: "light", y: { formatter: (v: number) => v.toLocaleString("fr-FR") } },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "left",
            markers: { width: 8, height: 8 },
            labels: { colors: "#374151" },
            fontSize: "12px",
        },
    };
}

function barOptions(color: string, labels: string[]): ApexCharts.ApexOptions {
    return {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", background: "transparent" },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
        colors: [color],
        dataLabels: { enabled: false },
        xaxis: {
            categories: labels,
            labels: { style: { colors: "#6b7280", fontSize: "11px" } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#6b7280", fontSize: "11px" } } },
        grid: { borderColor: "#f3f4f6", strokeDashArray: 3, xaxis: { lines: { show: false } } },
        tooltip: { theme: "light", y: { formatter: (v: number) => v.toLocaleString("fr-FR") } },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "left",
            markers: { width: 8, height: 8 },
            labels: { colors: "#374151" },
            fontSize: "12px",
        },
    };
}

// ── Chart sections ─────────────────────────────────────────────────────────────

function GooglePlaceCharts({ stats }: { stats: EnrichmentStatusStats }) {
    const labels = stats.weeklyData.map((w) => w.label);
    const done = stats.weeklyData.map((w) => w.googlePlaceDone);
    const pending = stats.weeklyData.map((w) => w.googlePlacePending);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Google Place récupérés par semaine</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · google_place done avec data</p>
                <div className="h-56">
                    <Chart type="bar" width="100%" height="100%" options={barOptions("#f59e0b", labels)} series={[{ name: "Récupérés", data: done }]} />
                </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Google Place en attente par semaine</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de l'event</p>
                <div className="h-56">
                    <Chart type="bar" width="100%" height="100%" options={barOptions("#f97316", labels)} series={[{ name: "En attente", data: pending }]} />
                </div>
            </div>
        </div>
    );
}

function GooglePlaceDFSCharts({ stats: initialStats }: { stats: EnrichmentStatusStats }) {
    const [dateField, setDateField] = useState<"createdAt" | "detectedAt">("createdAt");
    const [stats, setStats] = useState<EnrichmentStatusStats>(initialStats);
    const [loading, setLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const refresh = (field: "createdAt" | "detectedAt" = dateField) => {
        setLoading(true);
        getEnrichmentStatusStats(field === "createdAt" ? undefined : field)
            .then((s) => { setStats(s); setLastRefresh(new Date()); })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (dateField === "createdAt") {
            setStats(initialStats);
            return;
        }
        refresh("detectedAt");
    }, [dateField]);

    // Live refresh every 10 s so exact-match bulk progress is visible in real time
    useEffect(() => {
        const id = setInterval(() => refresh(), 10_000);
        return () => clearInterval(id);
    }, [dateField]);

    const labels = stats.weeklyData.map((w) => w.label);
    const monthlyLabels = stats.monthlyDFSData.map((m) => m.label);
    const dfs = stats.allTimeDFS;
    const total = dfs.pending + dfs.done + dfs.waitingCallback + dfs.approved + dfs.error + dfs.retry;
    const refreshHms = lastRefresh.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

    const dfsPending = stats.weeklyData.map((w) => w.googlePlaceDFSStatusPending);
    const dfsDone = stats.weeklyData.map((w) => w.googlePlaceDFSStatusDone);
    const dfsWaiting = stats.weeklyData.map((w) => w.googlePlaceDFSStatusWaitingCallback);
    const dfsApproved = stats.weeklyData.map((w) => w.googlePlaceDFSStatusApproved);
    const dfsError = stats.weeklyData.map((w) => w.googlePlaceDFSStatusError);
    const dfsRetry = stats.weeklyData.map((w) => w.googlePlaceDFSStatusRetry);

    const mPending = stats.monthlyDFSData.map((m) => m.googlePlaceDFSStatusPending);
    const mDone = stats.monthlyDFSData.map((m) => m.googlePlaceDFSStatusDone);
    const mWaiting = stats.monthlyDFSData.map((m) => m.googlePlaceDFSStatusWaitingCallback);
    const mApproved = stats.monthlyDFSData.map((m) => m.googlePlaceDFSStatusApproved);
    const mError = stats.monthlyDFSData.map((m) => m.googlePlaceDFSStatusError);
    const mRetry = stats.monthlyDFSData.map((m) => m.googlePlaceDFSStatusRetry);

    const dfsColors = ["#f97316", "#3b82f6", "#a855f7", "#22c55e", "#ef4444", "#f59e0b"];
    const dfsSeries = [
        { name: "Pending", data: dfsPending },
        { name: "Done", data: dfsDone },
        { name: "Waiting callback", data: dfsWaiting },
        { name: "Approved", data: dfsApproved },
        { name: "Error", data: dfsError },
        { name: "Retry", data: dfsRetry },
    ];

    const dateLabel = dateField === "createdAt" ? "création" : "détection";

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 flex-1">
                    <DFSKpiCard label="Pending" value={dfs.pending} total={total} valueColor="text-orange-500" />
                    <DFSKpiCard label="Done" value={dfs.done} total={total} valueColor="text-gray-700" />
                    <DFSKpiCard label="Waiting callback" value={dfs.waitingCallback} total={total} valueColor="text-purple-600" />
                    <DFSKpiCard label="Approved" value={dfs.approved} total={total} valueColor="text-green-600" />
                    <DFSKpiCard label="Error" value={dfs.error} total={total} valueColor="text-red-600" />
                    <DFSKpiCard label="Retry" value={dfs.retry} total={total} valueColor="text-amber-500" />
                </div>
            </div>
            <div className="flex items-center gap-4">
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 flex items-center gap-4 min-w-64">
                    <div>
                        <p className="text-xs text-blue-500 font-medium mb-1">Exact Match auto-approuvés</p>
                        <p className="text-2xl font-semibold text-blue-700">{(dfs.exactMatchApproved ?? 0).toLocaleString("fr-FR")}</p>
                        <p className="text-xs text-blue-400 mt-0.5">
                            {dfs.approved > 0 ? `${(((dfs.exactMatchApproved ?? 0) / dfs.approved) * 100).toFixed(1)}% des approuvés` : "—"}
                        </p>
                    </div>
                </div>
                <p className="text-xs text-gray-400">
                    {loading ? "Actualisation…" : `Actualisé à ${refreshHms} · toutes les 10 s`}
                </p>
            </div>

            <div className="flex items-center gap-1 self-start border border-gray-200 rounded-lg p-0.5">
                {(["createdAt", "detectedAt"] as const).map((f) => (
                    <button
                        key={f}
                        onClick={() => setDateField(f)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            dateField === f ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {f === "createdAt" ? "Par création" : "Par détection"}
                    </button>
                ))}
            </div>

            <div className={`border border-gray-200 rounded-lg p-4 ${loading ? "opacity-50" : ""}`}>
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Google Place DFS — statuts par semaine de {dateLabel}</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events groupés par semaine de {dateLabel}</p>
                <div className="h-72">
                    <Chart type="bar" width="100%" height="100%" options={stackedBarOptions(labels, dfsColors)} series={dfsSeries} />
                </div>
            </div>
            <div className={`border border-gray-200 rounded-lg p-4 ${loading ? "opacity-50" : ""}`}>
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Google Place DFS — statuts par mois de {dateLabel}</p>
                <p className="text-xs text-gray-400 mb-4">6 derniers mois · events groupés par mois de {dateLabel}</p>
                <div className="h-72">
                    <Chart type="bar" width="100%" height="100%" options={stackedBarOptions(monthlyLabels, dfsColors)} series={[
                        { name: "Pending", data: mPending },
                        { name: "Done", data: mDone },
                        { name: "Waiting callback", data: mWaiting },
                        { name: "Approved", data: mApproved },
                        { name: "Error", data: mError },
                        { name: "Retry", data: mRetry },
                    ]} />
                </div>
            </div>
        </div>
    );
}

function EmailCharts({ stats }: { stats: EnrichmentStatusStats }) {
    const labels = stats.weeklyData.map((w) => w.label);
    const monthlyLabels = stats.monthlyDFSData.map((m) => m.label);
    const email = stats.allTimeEmail;
    const total = email.done + email.pendingNoWebsite + email.pendingWithWebsite;

    const emailColors = ["#22c55e", "#ef4444", "#f97316"];
    const weeklySeries = [
        { name: "Done", data: stats.weeklyData.map((w) => w.emailDone) },
        { name: "Pending (sans site web)", data: stats.weeklyData.map((w) => w.emailPendingNoWebsite) },
        { name: "Pending (avec site web)", data: stats.weeklyData.map((w) => w.emailPendingWithWebsite) },
    ];
    const monthlySeries = [
        { name: "Done", data: stats.monthlyDFSData.map((m) => m.emailDone) },
        { name: "Pending (sans site web)", data: stats.monthlyDFSData.map((m) => m.emailPendingNoWebsite) },
        { name: "Pending (avec site web)", data: stats.monthlyDFSData.map((m) => m.emailPendingWithWebsite) },
    ];

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-3 gap-4">
                <DFSKpiCard label="Done" value={email.done} total={total} valueColor="text-green-600" />
                <DFSKpiCard label="Pending (sans site web)" value={email.pendingNoWebsite} total={total} valueColor="text-red-500" />
                <DFSKpiCard label="Pending (avec site web)" value={email.pendingWithWebsite} total={total} valueColor="text-orange-500" />
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Email — par semaine de création</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events groupés par semaine de création</p>
                <div className="h-72">
                    <Chart type="bar" width="100%" height="100%" options={stackedBarOptions(labels, emailColors)} series={weeklySeries} />
                </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Email — par mois de création</p>
                <p className="text-xs text-gray-400 mb-4">6 derniers mois · events groupés par mois de création</p>
                <div className="h-72">
                    <Chart type="bar" width="100%" height="100%" options={stackedBarOptions(monthlyLabels, emailColors)} series={monthlySeries} />
                </div>
            </div>
        </div>
    );
}

function AiConfidenceCharts({ stats }: { stats: EnrichmentStatusStats }) {
    const labels = stats.weeklyData.map((w) => w.label);
    const monthlyLabels = stats.monthlyDFSData.map((m) => m.label);
    const conf = stats.allTimeConfidence;
    const total = conf.high + conf.medium + conf.low + conf.unverified;
    const confColors = ["#22c55e", "#f59e0b", "#ef4444", "#9ca3af"];

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <DFSKpiCard label="High" value={conf.high} total={total} valueColor="text-green-600" />
                <DFSKpiCard label="Medium" value={conf.medium} total={total} valueColor="text-amber-500" />
                <DFSKpiCard label="Low" value={conf.low} total={total} valueColor="text-red-500" />
                <DFSKpiCard label="Non vérifié" value={conf.unverified} total={total} valueColor="text-gray-400" />
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">AI Confidence Level — par semaine</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · google_place_DFS done · regroupés par lastRunAt</p>
                <div className="h-72">
                    <Chart type="bar" width="100%" height="100%" options={stackedBarOptions(labels, confColors)} series={[
                        { name: "High", data: stats.weeklyData.map((w) => w.aiConfidenceLevelHigh) },
                        { name: "Medium", data: stats.weeklyData.map((w) => w.aiConfidenceLevelMedium) },
                        { name: "Low", data: stats.weeklyData.map((w) => w.aiConfidenceLevelLow) },
                        { name: "Non vérifié", data: stats.weeklyData.map((w) => w.aiConfidenceLevelUnverified) },
                    ]} />
                </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">AI Confidence Level — par mois</p>
                <p className="text-xs text-gray-400 mb-4">6 derniers mois · google_place_DFS done · regroupés par lastRunAt</p>
                <div className="h-72">
                    <Chart type="bar" width="100%" height="100%" options={stackedBarOptions(monthlyLabels, confColors)} series={[
                        { name: "High", data: stats.monthlyDFSData.map((m) => m.aiConfidenceLevelHigh) },
                        { name: "Medium", data: stats.monthlyDFSData.map((m) => m.aiConfidenceLevelMedium) },
                        { name: "Low", data: stats.monthlyDFSData.map((m) => m.aiConfidenceLevelLow) },
                        { name: "Non vérifié", data: stats.monthlyDFSData.map((m) => m.aiConfidenceLevelUnverified) },
                    ]} />
                </div>
            </div>
        </div>
    );
}

function LinkedinCharts({ stats }: { stats: EnrichmentStatusStats }) {
    const labels = stats.weeklyData.map((w) => w.label);
    const done = stats.weeklyData.map((w) => w.foundersAdded);
    const pending = stats.weeklyData.map((w) => w.linkedinPending);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Fondateurs ajoutés par semaine</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events avec au moins un fondateur</p>
                <div className="h-56">
                    <Chart type="bar" width="100%" height="100%" options={barOptions("#3b82f6", labels)} series={[{ name: "Fondateurs ajoutés", data: done }]} />
                </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">LinkedIn personnes en attente par semaine</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de la personne · sans lastRunAt</p>
                <div className="h-56">
                    <Chart type="bar" width="100%" height="100%" options={barOptions("#8b5cf6", labels)} series={[{ name: "LinkedIn en attente", data: pending }]} />
                </div>
            </div>
        </div>
    );
}

function BirthdateCharts({ stats }: { stats: EnrichmentStatusStats }) {
    const labels = stats.weeklyData.map((w) => w.label);
    const done = stats.weeklyData.map((w) => w.birthdateCompleted);
    const pending = stats.weeklyData.map((w) => w.birthdatePending);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Dates de naissance enrichies par semaine</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · personnes avec date de naissance enrichie</p>
                <div className="h-56">
                    <Chart type="bar" width="100%" height="100%" options={barOptions("#10b981", labels)} series={[{ name: "Dates de naissance", data: done }]} />
                </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">Dates de naissance en attente par semaine</p>
                <p className="text-xs text-gray-400 mb-4">8 dernières semaines · par date de création de la personne</p>
                <div className="h-56">
                    <Chart type="bar" width="100%" height="100%" options={barOptions("#14b8a6", labels)} series={[{ name: "En attente", data: pending }]} />
                </div>
            </div>
        </div>
    );
}

// ── Closing Checker (StatusMonitoring) section ────────────────────────────────

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(dateStr: string | null | undefined): string {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const today = new Date();
    const isToday =
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();
    if (isToday) {
        return `Aujourd'hui, ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
    }
    return `${d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}, ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

function daysAgo(dateStr: string | null | undefined): string {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "aujourd'hui";
    return `il y a ${diff} j`;
}

function VerifCard({
    title,
    info,
    icon: Icon,
}: {
    title: string;
    info: EstablishmentStatusStats["mostRecentVerification"];
    icon: ({ className }: { className?: string }) => JSX.Element;
}) {
    return (
        <div className="border border-gray-200 rounded-lg p-4 flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon className="w-5 h-5 text-gray-600" />
            </div>
            <div>
                <p className="text-xs text-gray-500 mb-0.5">{title}</p>
                <p className="text-base font-semibold text-gray-900">
                    {info ? formatTime(info.date) : "—"}
                </p>
                {info && (
                    <p className="text-xs text-gray-400 mt-0.5">
                        {info.name}
                        {info.location ? ` · ${info.location}` : ""}
                        {info.date ? ` · ${daysAgo(info.date)}` : ""}
                    </p>
                )}
            </div>
        </div>
    );
}

const STATUS_LABELS: Record<string, string> = {
    active: "Actif",
    closed: "Fermé",
    in_liquidation: "En liquidation",
    other: "Autre",
};

const STATUS_COLORS: Record<string, string> = {
    active: "text-green-600 bg-green-50 border-green-200",
    closed: "text-red-600 bg-red-50 border-red-200",
    in_liquidation: "text-amber-600 bg-amber-50 border-amber-200",
    other: "text-gray-600 bg-gray-50 border-gray-200",
};

function StatusBadge({ status }: { status: string | null }) {
    if (!status) return <span className="text-gray-400 text-xs">—</span>;
    const cls = STATUS_COLORS[status] ?? STATUS_COLORS.other;
    return (
        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded border ${cls}`}>
            {STATUS_LABELS[status] ?? status}
        </span>
    );
}

function PendingTable() {
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<ClosingCheckerPendingItem[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getClosingCheckerPending(page, 50)
            .then((res) => {
                setItems(res.data);
                setTotal(res.total);
                setTotalPages(res.totalPages);
            })
            .finally(() => setLoading(false));
    }, [page]);

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-900">
                    Établissements en attente
                </p>
                <p className="text-xs text-gray-400">{total.toLocaleString("fr-FR")} au total</p>
            </div>
            {loading ? (
                <p className="text-sm text-gray-400 p-4">Chargement…</p>
            ) : (
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-100">
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Nom</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Ville</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">SIREN</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Dernière vérif.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item._id} className="border-b border-gray-50 hover:bg-[#F9F6F3]">
                                <td className="px-4 py-2 text-gray-900 font-medium">{item.name}</td>
                                <td className="px-4 py-2 text-gray-500">{item.address?.city ?? "—"}</td>
                                <td className="px-4 py-2 text-gray-400 font-mono text-xs">{item.registration?.siren ?? "—"}</td>
                                <td className="px-4 py-2 text-gray-500">
                                    {item.registration?.last_status_update
                                        ? formatDate(item.registration.last_status_update)
                                        : <span className="text-red-400">Jamais</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                    <button
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                        className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
                    >
                        ← Précédent
                    </button>
                    <span className="text-xs text-gray-400">Page {page} / {totalPages}</span>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
                    >
                        Suivant →
                    </button>
                </div>
            )}
        </div>
    );
}

function ClosingCheckerContent({ stats }: { stats: EstablishmentStatusStats }) {
    const [activeTab, setActiveTab] = useState<"pipeline" | "quality" | "queue">("pipeline");
    const [siren, setSiren] = useState("999962806");
    const [checking, setChecking] = useState(false);
    const [checkResult, setCheckResult] = useState<SirenCheckResult | null>(null);
    const [checkError, setCheckError] = useState<string | null>(null);
    const [sweep, setSweep] = useState<ServiceOverviewItem | null>(null);

    useEffect(() => {
        getServicesOverview()
            .then((o) => setSweep(o.services.find((s) => s.key === "closingChecker") ?? null))
            .catch(() => {});
    }, []);

    const handleCheckSiren = async () => {
        if (!siren.trim()) return;
        setChecking(true);
        setCheckResult(null);
        setCheckError(null);
        try {
            const result = await checkSirenStatus(siren.trim());
            setCheckResult(result);
        } catch (e: any) {
            setCheckError(e?.response?.data?.error ?? "Erreur lors de la vérification");
        } finally {
            setChecking(false);
        }
    };

    const labels = stats.weeklyData.map((w) => w.label);
    const verifiedSeries = stats.weeklyData.map((w) => w.verified);
    const closedSeries = stats.weeklyData.map((w) => w.closed);
    const createdSeries = stats.weeklyData.map((w) => w.created);
    const enrichedSeries = stats.weeklyData.map((w) => w.enriched);

    const verifiedThisWeekSub = stats.verifiedThisWeekChange != null
        ? `${stats.verifiedThisWeekChange >= 0 ? "+" : ""}${stats.verifiedThisWeekChange}% vs sem. préc.`
        : "cette semaine";

    const coverage = sweep?.metrics.coveragePct ?? null;
    const cycleDays = sweep?.metrics.cycleDays ?? null;

    return (
        <>
            {sweep && (
                <div className="border border-gray-200 rounded-lg p-4 mb-6 bg-white">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span className="font-medium">
                            Couverture — établissements vérifiés depuis moins de {sweep.freshnessWindowDays ?? 7} jours
                        </span>
                        <span className="font-semibold text-gray-800">
                            {coverage === null ? "—" : `${coverage.toFixed(1)} %`}
                        </span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
                        <div
                            className={`h-full rounded-full transition-all ${
                                (coverage ?? 0) >= 80 ? "bg-green-500" : (coverage ?? 0) >= 30 ? "bg-amber-400" : "bg-red-400"
                            }`}
                            style={{ width: `${Math.min(100, coverage ?? 0)}%` }}
                        />
                    </div>
                    <p className="text-xs text-gray-400">
                        Balayage perpétuel du plus ancien au plus récent —{" "}
                        {(sweep.counts.fresh ?? 0).toLocaleString("fr-FR")} /{" "}
                        {(sweep.counts.population ?? 0).toLocaleString("fr-FR")} vérifiés dans la fenêtre · tour complet
                        de la base :{" "}
                        <span className="text-gray-600 font-medium">
                            {cycleDays === null ? "—" : `~${Math.round(cycleDays)} jours`}
                        </span>{" "}
                        au rythme actuel ({(sweep.counts.processed24h ?? 0).toLocaleString("fr-FR")} vérifications / 24 h)
                        {(sweep.counts.excluded ?? 0) > 0 && (
                            <> · {(sweep.counts.excluded ?? 0).toLocaleString("fr-FR")} actifs sans SIREN/SIRET jamais vérifiables</>
                        )}
                    </p>
                </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <StatCard label="Vérifiés cette semaine" value={stats.verifiedThisWeek} sub={verifiedThisWeekSub} />
                <StatCard label="Fermés détectés" value={stats.closedDetectedThisWeek} sub="cette semaine" valueColor="text-amber-600" />
                <StatCard
                    label="Non vérifiés +7 j"
                    value={stats.notVerifiedOver7Days}
                    sub="à traiter en priorité"
                    valueColor={stats.notVerifiedOver7Days > 0 ? "text-red-500" : "text-gray-900"}
                />
                <StatCard
                    label="Introuvables INSEE"
                    value={stats.inseeNotFound}
                    sub="SIREN 404 · déprioritisés"
                    valueColor={stats.inseeNotFound > 0 ? "text-orange-500" : "text-gray-900"}
                />
                <StatCard label="Total actifs suivis" value={stats.totalActiveTracked} sub="dans la base" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <VerifCard title="Vérification la plus récente" info={stats.mostRecentVerification} icon={Calendar} />
                <VerifCard title="Vérification la plus ancienne" info={stats.oldestVerification} icon={Calendar} />
            </div>

            <div className="flex gap-1 mb-4 border-b border-gray-200">
                {([
                    { key: "pipeline", label: "Pipeline" },
                    { key: "quality", label: "Qualité / Alertes" },
                    { key: "queue", label: `File d'attente (${stats.notVerifiedOver7Days.toLocaleString("fr-FR")})` },
                ] as { key: typeof activeTab; label: string }[]).map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                            activeTab === tab.key
                                ? "border-gray-900 text-gray-900"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === "pipeline" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Établissements créés par semaine</p>
                        <p className="text-xs text-gray-400 mb-4">8 dernières semaines</p>
                        <div className="h-56">
                            <Chart type="bar" width="100%" height="100%" options={barOptions("#6366f1", labels)} series={[{ name: "Créés", data: createdSeries }]} />
                        </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Établissements vérifiés par semaine</p>
                        <p className="text-xs text-gray-400 mb-4">8 dernières semaines</p>
                        <div className="h-56">
                            <Chart type="bar" width="100%" height="100%" options={barOptions("#3b82f6", labels)} series={[{ name: "Vérifiés", data: verifiedSeries }]} />
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "quality" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Enrichissements approuvés par semaine</p>
                        <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events avec enrichissement validé</p>
                        <div className="h-56">
                            <Chart type="bar" width="100%" height="100%" options={barOptions("#10b981", labels)} series={[{ name: "Enrichis", data: enrichedSeries }]} />
                        </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Établissements fermés détectés par semaine</p>
                        <p className="text-xs text-gray-400 mb-4">8 dernières semaines</p>
                        <div className="h-56">
                            <Chart type="bar" width="100%" height="100%" options={barOptions("#ef4444", labels)} series={[{ name: "Fermés", data: closedSeries }]} />
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "queue" && <PendingTable />}

            <div className="mt-4 border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-3">Tester une vérification INSEE</p>
                <div className="flex items-center gap-2 mb-4">
                    <input
                        type="text"
                        value={siren}
                        onChange={(e) => setSiren(e.target.value)}
                        placeholder="SIREN (9 chiffres)"
                        className="border border-gray-200 rounded px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-1 focus:ring-gray-300"
                    />
                    <button
                        onClick={handleCheckSiren}
                        disabled={checking || !siren.trim()}
                        className="bg-gray-900 text-white text-sm px-4 py-1.5 rounded hover:bg-gray-800 disabled:opacity-50"
                    >
                        {checking ? "Vérification…" : "Lancer"}
                    </button>
                </div>

                {checkError && <p className="text-sm text-red-500">{checkError}</p>}

                {checkResult && (
                    <div className="text-sm space-y-3">
                        <p className="font-medium text-gray-900">
                            {checkResult.establishment.name}
                            <span className="ml-2 text-xs text-gray-400 font-normal">{checkResult.establishment._id}</span>
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="border border-gray-200 rounded p-3">
                                <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Avant</p>
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">Statut</span>
                                        <StatusBadge status={checkResult.before.status} />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">Actif</span>
                                        <span className="text-xs text-gray-700">
                                            {checkResult.before.is_active === null ? "—" : checkResult.before.is_active ? "Oui" : "Non"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">Dernière vérif.</span>
                                        <span className="text-xs text-gray-700">
                                            {checkResult.before.last_status_update ? formatDate(checkResult.before.last_status_update) : "Jamais"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">INSEE</span>
                                        {checkResult.before.insee_not_found
                                            ? <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">Introuvable</span>
                                            : <span className="text-xs text-gray-400">—</span>
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="border border-gray-200 rounded p-3">
                                <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">
                                    Après
                                    {checkResult.changed && (
                                        <span className="ml-2 text-amber-600 font-semibold normal-case">· Statut modifié</span>
                                    )}
                                </p>
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">Statut</span>
                                        <StatusBadge status={checkResult.after.status} />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">Actif</span>
                                        <span className="text-xs text-gray-700">
                                            {checkResult.after.is_active === null ? "—" : checkResult.after.is_active ? "Oui" : "Non"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">Dernière vérif.</span>
                                        <span className="text-xs text-gray-700">
                                            {checkResult.after.last_status_update ? formatDate(checkResult.after.last_status_update) : "—"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 w-28">INSEE</span>
                                        {checkResult.after.insee_not_found
                                            ? <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">Introuvable</span>
                                            : <span className="text-xs text-gray-400">—</span>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

// ── BODACC History detail ──────────────────────────────────────────────────────

function timeAgo(dateStr: string | null): { text: string; color: string } {
    if (!dateStr) return { text: "jamais", color: "text-red-500" };
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffSec = Math.floor(diffMs / 1_000);
    const diffMin = Math.floor(diffMs / 60_000);
    const diffH   = Math.floor(diffMs / 3_600_000);
    if (diffSec < 60)  return { text: `il y a ${diffSec} s`,  color: "text-green-600" };
    if (diffH   < 1)   return { text: `il y a ${diffMin} min`, color: "text-green-600" };
    if (diffH   < 24)  return { text: `il y a ${diffH} h`,    color: "text-amber-600" };
    return { text: `il y a ${Math.floor(diffH / 24)} j`, color: "text-red-500" };
}

function fmt(d?: string | null): string {
    if (!d) return "—";
    try { return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(d)); }
    catch { return d; }
}

function BodaccHistoryContent({ stats }: { stats: BodaccDetailStats }) {
    const navigate = useNavigate();
    const ago = timeAgo(stats.lastCheckedAt);
    const pct = stats.totalWithSiren > 0
        ? Math.min(100, (stats.checkedToday / stats.totalWithSiren) * 100)
        : 0;

    return (
        <div className="flex flex-col gap-6">
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    label="Vérifiés aujourd'hui"
                    value={stats.checkedToday}
                    sub={`sur ${stats.totalWithSiren.toLocaleString("fr-FR")} établissements`}
                    valueColor="text-green-600"
                />
                <StatCard
                    label="Restants aujourd'hui"
                    value={stats.remaining}
                    sub="à parcourir dans ce cycle"
                    valueColor={stats.remaining > 0 ? "text-amber-600" : "text-green-600"}
                />
                <StatCard
                    label="Avec annonces BODACC"
                    value={stats.totalWithBodacc}
                    sub={`${stats.totalWithSiren > 0 ? ((stats.totalWithBodacc / stats.totalWithSiren) * 100).toFixed(1) : 0}% du total`}
                    valueColor="text-blue-600"
                />
                <StatCard
                    label="Dernier check"
                    value={ago.text}
                    sub={stats.lastCheckedAt ? fmt(stats.lastCheckedAt) : "—"}
                    valueColor={ago.color}
                />
            </div>

            {/* Progress bar */}
            <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between text-xs text-gray-500 mb-2">
                    <span className="font-medium">Progression du cycle du jour</span>
                    <span>{pct.toFixed(1)} %</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                    Cadence (24 h) : {stats.processedLast24h.toLocaleString("fr-FR")} checks
                    {stats.processedLast24h > 0 && (
                        <> · ~{(stats.processedLast24h / 1440).toFixed(1)} / min · ~{(stats.processedLast24h / 24).toFixed(0)} / h</>
                    )}
                </p>
            </div>

            {/* Recent activity table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-semibold text-gray-900">Derniers établissements vérifiés</p>
                    <button
                        onClick={() => navigate("/bo/boddac-history")}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Voir tout l'historique
                    </button>
                </div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Nom</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 hidden sm:table-cell">Ville</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 hidden md:table-cell">SIREN</th>
                            <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Annonces</th>
                            <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Vérifié le</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats.recentlyChecked.map((row) => (
                            <tr
                                key={row._id}
                                className="border-b border-gray-50 hover:bg-[#F9F6F3] cursor-pointer"
                                onClick={() => navigate(`/establishments/${row._id}`)}
                            >
                                <td className="px-4 py-2 text-gray-900 font-medium truncate max-w-[180px]">{row.name}</td>
                                <td className="px-4 py-2 text-gray-500 hidden sm:table-cell">{row.city ?? "—"}</td>
                                <td className="px-4 py-2 text-gray-400 font-mono text-xs hidden md:table-cell">{row.siren ?? "—"}</td>
                                <td className="px-4 py-2 text-right">
                                    {row.bodaccCount > 0
                                        ? <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">{row.bodaccCount}</span>
                                        : <span className="text-xs text-gray-400">0</span>}
                                </td>
                                <td className="px-4 py-2 text-right text-xs text-gray-400">{fmt(row.bodaccCheckedAt)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Label mapping ──────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
    google_place: "Google Place",
    google_place_DFS: "Google Place DFS",
    website_content_OTS: "Website content (OTS)",
    website: "Website",
    linkedin: "LinkedIn",
    birthdate: "Birthdate",
    eventsWithNoPeople: "Events sans personnes",
    aiConfidencePending: "AI Confidence",
    closing_checker: "Closing Checker",
    bodaccHistory: "BODACC History",
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ServiceDetail() {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();

    const isClosingChecker  = type === "closing_checker";
    const isBodaccHistory   = type === "bodaccHistory";
    const [enrichmentStats, setEnrichmentStats] = useState<EnrichmentStatusStats | null>(null);
    const [statusStats, setStatusStats]         = useState<EstablishmentStatusStats | null>(null);
    const [bodaccStats, setBodaccStats]         = useState<BodaccDetailStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        if (isClosingChecker) {
            getEstablishmentStatusStats().then(setStatusStats).finally(() => setLoading(false));
        } else if (isBodaccHistory) {
            getBodaccDetailStats().then(setBodaccStats).finally(() => setLoading(false));
        } else {
            getEnrichmentStatusStats().then(setEnrichmentStats).finally(() => setLoading(false));
        }
    }, [type]);

    const label = TYPE_LABELS[type ?? ""] ?? type ?? "Service";

    const renderCharts = () => {
        if (isClosingChecker) {
            if (!statusStats) return null;
            return <ClosingCheckerContent stats={statusStats} />;
        }
        if (isBodaccHistory) {
            if (!bodaccStats) return null;
            return <BodaccHistoryContent stats={bodaccStats} />;
        }
        if (!enrichmentStats) return null;
        switch (type) {
            case "google_place": return <GooglePlaceCharts stats={enrichmentStats} />;
            case "google_place_DFS": return <GooglePlaceDFSCharts stats={enrichmentStats} />;
            case "website_content_OTS": return <EmailCharts stats={enrichmentStats} />;
            case "aiConfidencePending": return <AiConfidenceCharts stats={enrichmentStats} />;
            case "linkedin": return <LinkedinCharts stats={enrichmentStats} />;
            case "birthdate": return <BirthdateCharts stats={enrichmentStats} />;
            default: return (
                <div className="border border-gray-200 rounded-lg p-8 text-center">
                    <p className="text-sm text-gray-500">Pas de graphiques disponibles pour ce service.</p>
                </div>
            );
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <BONavigation />

                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={() => navigate("/bo/services")}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Services
                    </button>
                    <span className="text-gray-300">/</span>
                    <h1 className="text-xl font-semibold text-gray-900">{label}</h1>
                </div>

                {loading ? (
                    <div className="text-sm text-gray-400">Chargement…</div>
                ) : (
                    renderCharts()
                )}
            </div>
        </div>
    );
}
